import { CloseIcon } from '@chakra-ui/icons';
import { Tag, TagCloseButton, TagLabel } from '@chakra-ui/react';
import React from 'react';

import {
  CmButton,
  CmCard,
  CmFlex,
  CmFormControl,
  CmFormErrorMessage,
  CmFormLabel,
  CmIconButton,
  CmInput,
  CmLabelAndInput,
  CmLabelAndSelect,
  CmModal,
  CmModalBody,
  CmModalFooter,
  CmStack,
  CmTd,
  CmText,
} from '@/components/uiParts';
import { deliveryAreaForm } from '@/pages/delivery/in-house/service';
import { AuthStoreDeliveryAreas } from '@/pages/delivery/in-house/types';
import { DeliveryAreaFormError } from '@/pages/delivery/in-house/validate';

type DeliveryAreaModalProps = {
  isOpen: boolean;
  onClose: () => void;
  error: DeliveryAreaFormError;
  storesDeliveryAreas: AuthStoreDeliveryAreas;
  handleDeliveryAreaUpdate: (
    authStoreDeliveryAreas: AuthStoreDeliveryAreas
  ) => void;
  handleAddDeliveryArea: () => void;
  handleDeleteDeliveryArea: (index: number) => void;
  handleUpdatePostalCodes: (index: number, postalCodes: string[]) => void;
  handleChangeName: (index: number, value: string) => void;
  handleChangePostalCode: (index: number, value: string) => void;
  handleChangeDeliveryFee: (index: number, value: string) => void;
  handleChangeDeliveryAsapDuration: (index: number, value: number) => void;
};

// 最短配達時間のオプション(5分~120分)
const ASAP_DURATION_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  id: (i + 1) * 5,
  title: `${(i + 1) * 5}分`,
}));

export const DeliveryAreaModal: React.FC<DeliveryAreaModalProps> = ({
  isOpen,
  onClose,
  error,
  storesDeliveryAreas,
  handleDeliveryAreaUpdate,
  handleAddDeliveryArea,
  handleDeleteDeliveryArea,
  handleUpdatePostalCodes,
  handleChangeName,
  handleChangePostalCode,
  handleChangeDeliveryFee,
  handleChangeDeliveryAsapDuration,
}) => {
  const { auth_store_name, delivery_areas } = storesDeliveryAreas || {};

  const handleDeletePostalCode = (
    areaIndex: number,
    postalIndex: number,
    postalCodes: string[]
  ) => {
    const updatedPostalCodes = postalCodes.filter(
      (_, idx) => idx !== postalIndex
    );
    handleUpdatePostalCodes(areaIndex, updatedPostalCodes);
  };

  // 配達エリアの未入力チェック
  const isEmpty = delivery_areas?.some((area) => {
    return area.name === '' || area.postal_codes.length === 0;
  });

  // 配達エリアの登録フォームのエラーチェック
  const disabled = error.some((areaError) => {
    return (
      areaError.name.isError ||
      areaError.postalCode.isError ||
      areaError.deliveryFee.isError
    );
  });

  return (
    <CmModal
      {...{ isOpen, onClose }}
      title={`${auth_store_name}の配達エリア編集`}
    >
      <CmModalBody pb="10">
        <CmStack spacing="15px">
          {delivery_areas?.map((area, index) => (
            <CmCard key={index}>
              <CmFlex flexDirection="row-reverse">
                <CmIconButton
                  aria-label="削除"
                  backgroundColor="transparent"
                  icon={<CloseIcon />}
                  onClick={() => handleDeleteDeliveryArea(index)}
                />
                {/* 配達エリア名 */}
                <CmLabelAndInput
                  isRequired
                  isInvalid={error[index][deliveryAreaForm.name.key].isError}
                  id={`area-name-${index}`}
                  label={`配達エリア名`}
                  fontWeight="bold"
                  value={area['name'] || ''}
                  onChange={(e) => {
                    handleChangeName(index, e.target.value);
                  }}
                >
                  <CmFormErrorMessage>
                    {error[index][deliveryAreaForm.name.key].message}
                  </CmFormErrorMessage>
                </CmLabelAndInput>
              </CmFlex>
              {/* 郵便番号 */}
              <CmFormControl
                mt={3}
                isInvalid={
                  error[index][deliveryAreaForm.postalCode.key].isError
                }
              >
                <CmFormLabel
                  htmlFor={`area-name-${index}`}
                  isRequired
                  fontWeight="bold"
                >
                  郵便番号
                </CmFormLabel>
                <CmText fontSize="xs" color="gray.500">
                  ハイフン (-) を含めずに入力してください。
                  <br />
                  アスタリスク (*)
                  を使用すると、連続する郵便番号の範囲を指定できます。
                </CmText>
                <CmInput
                  id={`area-name-${index}`}
                  defaultValue={''}
                  placeholder="123456*"
                  my={1}
                  onBlur={(e) => {
                    handleChangePostalCode(index, e.target.value.trim());
                    if (
                      !error[index][deliveryAreaForm.postalCode.key].isError
                    ) {
                      e.target.value = '';
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ',') {
                      e.preventDefault();
                      const input = e.target as HTMLInputElement;
                      if (input.value.trim()) {
                        handleChangePostalCode(index, input.value.trim());
                        if (
                          !error[index][deliveryAreaForm.postalCode.key].isError
                        ) {
                          input.value = '';
                        }
                      }
                    }
                  }}
                />
                <CmFormErrorMessage>
                  {error[index][deliveryAreaForm.postalCode.key].message}
                </CmFormErrorMessage>

                <CmTd padding={1} mt={3}>
                  {area.postal_codes.map((postalCode, idx) => (
                    <Tag
                      key={idx}
                      mr={2}
                      mb={2}
                      borderRadius="full"
                      variant="outline"
                      colorScheme="gray"
                    >
                      <TagLabel>{postalCode}</TagLabel>
                      <TagCloseButton
                        onClick={() =>
                          handleDeletePostalCode(index, idx, area.postal_codes)
                        }
                      />
                    </Tag>
                  ))}
                </CmTd>
              </CmFormControl>

              <CmFlex
                justifyContent="space-between"
                gridGap={6}
                alignItems="center"
                mt={3}
              >
                {/* 配送手数料 */}
                <CmLabelAndInput
                  isRequired
                  id={`area-deliveryfee-${index}`}
                  isInvalid={
                    error[index][deliveryAreaForm.deliveryFee.key].isError
                  }
                  label={`配送手数料`}
                  fontWeight="bold"
                  value={`¥${area.delivery_fee}`}
                  onChange={(e) => {
                    handleChangeDeliveryFee(index, e.target.value);
                  }}
                >
                  <CmFormErrorMessage>
                    {error[index][deliveryAreaForm.deliveryFee.key].message}
                  </CmFormErrorMessage>
                </CmLabelAndInput>

                {/* 最短配達時間 */}
                <CmLabelAndSelect
                  isRequired
                  fontWeight="bold"
                  id={`area-asap-duration-${index}`}
                  label={`最短配達時間`}
                  value={area.asap_duration}
                  onChange={(e) => {
                    handleChangeDeliveryAsapDuration(
                      index,
                      Number(e.target.value)
                    );
                  }}
                >
                  <option hidden />
                  {ASAP_DURATION_OPTIONS.map((asapDurationOption) => {
                    const { id, title } = asapDurationOption;
                    return (
                      <option key={id} value={id}>
                        {title}
                      </option>
                    );
                  })}
                </CmLabelAndSelect>
              </CmFlex>
            </CmCard>
          ))}
        </CmStack>

        <CmFlex mt={6} flexDirection="row-reverse">
          <CmButton
            variant="outline"
            onClick={() => handleAddDeliveryArea()}
            disabled={disabled || isEmpty}
          >
            配達エリアを追加
          </CmButton>
        </CmFlex>
      </CmModalBody>

      <CmModalFooter>
        <CmButton
          width={100}
          mr={4}
          onClick={() => handleDeliveryAreaUpdate(storesDeliveryAreas)}
          disabled={disabled || isEmpty}
        >
          登録
        </CmButton>
        <CmButton variant="outline" onClick={onClose}>
          キャンセル
        </CmButton>
      </CmModalFooter>
    </CmModal>
  );
};
